import React, { useEffect, useRef, useState, useCallback } from "react";
import ko from "knockout";
import clsx from "clsx";
import { Button } from "@material-ui/core";
import { useStyles } from "./styles";
import ReportQueueControlsWrapper from "./ReportQueueControlsWrapper";
import { computeRoutingDecision } from "./reportV2RoutingUtils";

/**
 * Observes DevExpress parameter changes and conditionally renders:
 * - 'immediate': Material UI Submit + Reset buttons that trigger DevExpress actions
 * - 'queue': existing ReportQueueControlsWrapper (format dropdown + Submit To Queue)
 *
 * DevExpress native Reset/Submit buttons are hidden via CSS (hide-toolbar).
 */
const ReportV2RoutingWrapper = ({
  reportName,
  currentUser,
  reportQueueService,
  enqueueSnackbar,
  history,
  thresholdDays = 7,
  onRoutingDecisionChange,
}) => {
  const [routingDecision, setRoutingDecision] = useState(null);
  const observerRef = useRef(null);
  const subscriptionsRef = useRef([]);
  const classes = useStyles();

  const evaluateRouting = useCallback((parametersModel) => {
    if (!parametersModel || !parametersModel._parameters) return;

    let startDate = null;
    let endDate = null;

    parametersModel._parameters.forEach((param) => {
      const key = param.path?.toLowerCase();
      if (key === "startdate") {
        startDate = typeof param.value === "function" ? param.value() : param.value;
      } else if (key === "enddate") {
        endDate = typeof param.value === "function" ? param.value() : param.value;
      }
    });

    const decision = computeRoutingDecision(startDate, endDate, thresholdDays);
    setRoutingDecision(decision);
    if (onRoutingDecisionChange) {
      onRoutingDecisionChange(decision);
    }
  }, [thresholdDays, onRoutingDecisionChange]);

  const subscribeToParameterChanges = useCallback((parametersModel) => {
    subscriptionsRef.current.forEach((sub) => sub.dispose());
    subscriptionsRef.current = [];

    if (!parametersModel || !parametersModel._parameters) return;

    parametersModel._parameters.forEach((param) => {
      const key = param.path?.toLowerCase();
      if ((key === "startdate" || key === "enddate") && typeof param.value === "function" && param.value.subscribe) {
        const sub = param.value.subscribe(() => {
          evaluateRouting(parametersModel);
        });
        subscriptionsRef.current.push(sub);
      }
    });
  }, [evaluateRouting]);

  useEffect(() => {
    if (observerRef.current) return;

    const observer = new MutationObserver(() => {
      const resetButton = document.querySelector(".dxrdp-parameters-reset");
      if (!resetButton) return;

      const viewportEl = document.querySelector(".viewport");
      if (!viewportEl) return;

      try {
        const rawModel = ko.dataFor(viewportEl);
        if (!rawModel || !rawModel.viewerModel) return;

        const symbols = Object.getOwnPropertySymbols(rawModel.viewerModel);
        const latestValueSymbol = symbols.find(
          (s) => s.toString() === "Symbol(_latestValue)"
        );
        if (!latestValueSymbol) return;

        const previewModel = rawModel.viewerModel[latestValueSymbol];
        const parametersModel = previewModel?.parametersModel;
        if (!parametersModel) return;

        evaluateRouting(parametersModel);
        subscribeToParameterChanges(parametersModel);

        observer.disconnect();
        observerRef.current = null;
      } catch (e) {
        // Model not ready yet
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      subscriptionsRef.current.forEach((sub) => sub.dispose());
      subscriptionsRef.current = [];
    };
  }, [evaluateRouting, subscribeToParameterChanges]);

  /** Trigger the hidden DevExpress Submit button click */
  const handleSubmit = useCallback(() => {
    const dxSubmit = document.querySelector(".dxrdp-parameters-submit");
    if (dxSubmit) dxSubmit.click();
  }, []);

  /** Trigger the hidden DevExpress Reset button click */
  const handleReset = useCallback(() => {
    const dxReset = document.querySelector(".dxrdp-parameters-reset");
    if (dxReset) dxReset.click();
  }, []);

  if (routingDecision === "queue") {
    return (
      <ReportQueueControlsWrapper
        reportName={reportName}
        currentUser={currentUser}
        reportQueueService={reportQueueService}
        enqueueSnackbar={enqueueSnackbar}
        history={history}
        onReset={handleReset}
      />
    );
  }

  if (routingDecision === "immediate") {
    return (
      <>
        <Button
          className={clsx("resetBtn", classes.btnControl)}
          variant="outlined"
          color="primary"
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          className={clsx("submitBtn", classes.btnControl)}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </>
    );
  }

  return null;
};

export default ReportV2RoutingWrapper;
